import type { Uuid } from '../types/api';
import type {
  ChannelDraft,
  Chat,
  ChatMember,
  GroupDraft,
  MembershipPatch,
} from '../types/chat';
import type { LiniyaHttpClient } from '../http/client';

export class ChatsApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  async list(): Promise<Chat[]> {
    const response = await this.http.request<{ chats: Chat[] }>('/api/v1/chats/');
    return response.chats;
  }

  async detail(chatId: Uuid): Promise<Chat> {
    const response = await this.http.request<{ chat: Chat }>(`/api/v1/chats/${chatId}/`);
    return response.chat;
  }

  /** Open the direct chat with someone, or return the existing one. */
  async openDirect(participantUserId: Uuid): Promise<Chat> {
    const response = await this.http.request<{ chat: Chat }>('/api/v1/chats/direct/', {
      method: 'POST',
      body: { participant_user_id: participantUserId },
    });
    return response.chat;
  }

  async createGroup(draft: GroupDraft): Promise<Chat> {
    const response = await this.http.request<{ chat: Chat }>('/api/v1/chats/groups/', {
      method: 'POST',
      body: draft,
    });
    return response.chat;
  }

  async createChannel(draft: ChannelDraft): Promise<Chat> {
    const response = await this.http.request<{ chat: Chat }>('/api/v1/chats/channels/', {
      method: 'POST',
      body: draft,
    });
    return response.chat;
  }

  async subscribeToChannel(chatId: Uuid): Promise<ChatMember> {
    const response = await this.http.request<{ member: ChatMember }>(
      `/api/v1/chats/channels/${chatId}/subscribe/`,
      { method: 'POST', body: {} },
    );
    return response.member;
  }

  async updateMembership(chatId: Uuid, patch: MembershipPatch): Promise<Chat> {
    const response = await this.http.request<{ chat: Chat }>(
      `/api/v1/chats/${chatId}/membership/`,
      { method: 'PATCH', body: patch },
    );
    return response.chat;
  }

  async addMember(chatId: Uuid, userId: Uuid, role?: string): Promise<ChatMember> {
    const response = await this.http.request<{ member: ChatMember }>(
      `/api/v1/chats/${chatId}/members/`,
      { method: 'POST', body: role ? { user_id: userId, role } : { user_id: userId } },
    );
    return response.member;
  }

  async updateMember(chatId: Uuid, userId: Uuid, action: string): Promise<ChatMember> {
    const response = await this.http.request<{ member: ChatMember }>(
      `/api/v1/chats/${chatId}/members/${userId}/`,
      { method: 'PATCH', body: { action } },
    );
    return response.member;
  }

  async leave(chatId: Uuid): Promise<void> {
    await this.http.request<void>(`/api/v1/chats/${chatId}/leave/`, {
      method: 'POST',
      body: {},
    });
  }

  /** Hide history for this account only; other members keep their copy. */
  async clearHistory(chatId: Uuid): Promise<void> {
    await this.http.request<void>(`/api/v1/chats/${chatId}/clear/`, {
      method: 'POST',
      body: {},
    });
  }

  async setBlocked(chatId: Uuid, blocked: boolean): Promise<boolean> {
    const response = await this.http.request<{ blocked: boolean }>(
      `/api/v1/chats/${chatId}/block/`,
      { method: 'POST', body: { blocked } },
    );
    return response.blocked;
  }
}
